import { Card } from "@components/cards/card";
import { IRaffleScheduledDrawResponseDTO } from "@hooks/dtos/raffles/IRaffleDefaultDrawScheduleDTO";
import {
  RaffleDefaultDrawScheduleStatusEnum,
  RaffleDefaultDrawScheduleTypeEnum,
} from "@hooks/enums/raffle_default_draw_schedule";
import {
  getRaffleDefaultDrawScheduleStatus,
  getRaffleDefaultDrawScheduleType,
} from "@hooks/functions/Raffle/status";
import { formatName } from "@hooks/functions/formatName";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import {
  FaCalendarCheck,
  FaCheckCircle,
  FaClock,
  FaTrophy,
} from "react-icons/fa";
import { FaLandmark } from "react-icons/fa6";

interface IProps {
  raffleKey: string;
  resultScheduleEndpoint?: string;
}

function formatDateTime(date?: string | null) {
  if (!date || !dayjs(date).isValid()) return "-";

  return dayjs(date).format("DD/MM/YYYY [às] HH:mm");
}

function getWinnerName(resultInfo?: IRaffleScheduledDrawResponseDTO) {
  const name = resultInfo?.schedule?.winner?.ticketData?.clientData?.name;
  if (name) return formatName(name);

  return "-";
}

function getWinnerAtSign(resultInfo?: IRaffleScheduledDrawResponseDTO) {
  const atSign = resultInfo?.schedule?.winner?.ticketData?.clientData?.atSign;
  if (atSign) return `@${atSign}`;

  return "-";
}

export function RaffleOwnerResultInfo({
  raffleKey,
  resultScheduleEndpoint = "/customer/raffle/default/result/schedule",
}: IProps) {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [resultInfo, setResultInfo] =
    useState<IRaffleScheduledDrawResponseDTO>();
  const [isLoading, setIsLoading] = useState(true);

  async function loadResultInfo() {
    setIsLoading(true);

    try {
      const response = await api.get(
        `${resultScheduleEndpoint}?key=${raffleKey}`,
      );
      setResultInfo(response.data);
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar o resultado da campanha.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (raffleKey) {
      loadResultInfo();
    }
  }, [raffleKey]);

  const schedule = resultInfo?.schedule;
  const scheduleTypeValue = resultInfo?.type || schedule?.type;
  const scheduleType = getRaffleDefaultDrawScheduleType(scheduleTypeValue);
  const scheduleStatus = getRaffleDefaultDrawScheduleStatus(schedule?.status);
  const isLottery =
    scheduleTypeValue === RaffleDefaultDrawScheduleTypeEnum.LOTTERY;
  const winnerTicket = schedule?.winner?.ticketData;
  const winnerSteps = schedule?.winnerResult?.steps || [];

  return (
    <Card>
      <div className="card-header">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <div>
            <div className="card-title">Resultado da Campanha</div>
            <small className="text-muted">
              Informações finais do sorteio desta campanha.
            </small>
          </div>
          {schedule && (
            <span
              className={`badge bg-${scheduleStatus.color} align-self-start`}
              title={scheduleStatus.title}
            >
              {scheduleStatus.text}
            </span>
          )}
        </div>
      </div>

      <div className="card-body">
        {isLoading ? (
          <Skeleton height={180} count={1} />
        ) : !resultInfo?.hasSchedule || !schedule ? (
          <div className="alert alert-warning mb-0">
            Nenhum agendamento de sorteio foi encontrado para esta campanha.
          </div>
        ) : (
          <>
            <div className="row g-3">
              <div className="col-md-3">
                <div className="border rounded p-3 h-100">
                  <small className="text-muted">Tipo</small>
                  <div className="fw-bold" title={scheduleType.title}>
                    {scheduleType.text}
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-3 h-100">
                  <small className="text-muted">Status</small>
                  <div className="fw-bold" title={scheduleStatus.title}>
                    {scheduleStatus.text}
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-3 h-100">
                  <small className="text-muted">
                    {isLottery ? "Concurso" : "Data do sorteio"}
                  </small>
                  <div className="fw-bold">
                    {isLottery
                      ? schedule.lottery?.contestNumber || "-"
                      : formatDateTime(schedule.random?.drawAt)}
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-3 h-100">
                  <small className="text-muted">Processado em</small>
                  <div className="fw-bold">
                    {formatDateTime(schedule.processedAt)}
                  </div>
                </div>
              </div>
            </div>

            {isLottery && (
              <div className="row g-3 mt-1">
                <div className="col-md-6">
                  <div className="bg-light rounded p-3 h-100">
                    <FaLandmark className="me-2 text-primary" />
                    <small className="text-muted">Combinação usada</small>
                    <div className="fw-bold">
                      {schedule.lottery?.lotteryCombination || "-"}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="bg-light rounded p-3 h-100">
                    <FaCalendarCheck className="me-2 text-primary" />
                    <small className="text-muted">Número da Loteria</small>
                    <div className="fw-bold">
                      {schedule.lottery?.lotteryNumber || "-"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {schedule.status === RaffleDefaultDrawScheduleStatusEnum.DONE &&
              winnerTicket && (
                <div className="border border-success rounded mt-3 overflow-hidden">
                  <div className="bg-success-subtle border-bottom border-success p-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
                      <div>
                        <div className="d-flex align-items-center gap-2 fw-bold">
                          <FaTrophy className="text-success" />
                          Ganhador definido
                        </div>
                        <small className="text-muted">
                          O sorteio foi processado e o ticket vencedor já foi
                          identificado.
                        </small>
                      </div>
                      <div
                        className="text-md-end"
                        title="Número do ticket vencedor da campanha."
                      >
                        <small className="text-muted d-block">
                          Ticket vencedor
                        </small>
                        <span className="fs-4 fw-bold text-success">
                          {winnerTicket.ticket || "-"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3">
                    <div className="row g-3 align-items-stretch">
                      <div className="col-lg-5">
                        <div className="bg-light rounded p-3 h-100">
                          <small className="text-muted">Ganhador</small>
                          <div
                            className="fs-5 fw-bold"
                            title="Nome do comprador vencedor."
                          >
                            {getWinnerName(resultInfo)}
                          </div>
                          <div
                            className="text-primary fw-semibold"
                            title="@arroba do comprador vencedor."
                          >
                            {getWinnerAtSign(resultInfo)}
                          </div>
                          {winnerTicket.boughtAt && (
                            <small className="d-block mt-2 text-muted">
                              Compra realizada em{" "}
                              {formatDateTime(winnerTicket.boughtAt)}.
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-7">
                        <div className="row g-3 h-100">
                          <div className="col-md-4">
                            <div className="bg-light rounded p-3 h-100">
                              <small className="text-muted">Ordem</small>
                              <div
                                className="fs-5 fw-bold"
                                title="Número calculado pelo resultado do sorteio."
                              >
                                {schedule.winner.order ||
                                  winnerTicket.order ||
                                  "-"}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="bg-light rounded p-3 h-100">
                              <small className="text-muted">Ticket ID</small>
                              <div
                                className="fs-5 fw-bold"
                                title="Identificador interno do ticket vencedor."
                              >
                                {schedule.winner.ticketId ||
                                  winnerTicket.id ||
                                  "-"}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="bg-light rounded p-3 h-100">
                              <small className="text-muted">Origem</small>
                              <div
                                className="fs-5 fw-bold"
                                title="Origem usada para definir o ganhador."
                              >
                                {schedule.winner.type === "r"
                                  ? "Aleatório"
                                  : isLottery
                                    ? "Loteria"
                                    : "Aleatório"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {winnerSteps.length > 0 && (
              <div className="border rounded p-3 mt-3">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <FaCheckCircle className="text-success" />
                  <div>
                    <div className="fw-bold">Como o resultado foi definido</div>
                    <small className="text-muted">
                      Etapas calculadas até encontrar o ticket vencedor.
                    </small>
                  </div>
                </div>

                <div className="list-group">
                  {winnerSteps.map((step) => (
                    <div
                      className="list-group-item d-flex gap-3 align-items-start"
                      key={step.index}
                    >
                      <span
                        className="badge bg-primary rounded-pill flex-shrink-0 mt-1"
                        title="Ordem da etapa no processamento do resultado."
                      >
                        {step.index}
                      </span>
                      <div className="flex-grow-1">
                        <div className="fw-semibold">
                          {step.messages?.["pt-br"] || "-"}
                        </div>
                        {(step.order || step.ticket) && (
                          <small className="text-muted">
                            Ordem {step.order || "-"} | Ticket{" "}
                            {step.ticket || "-"}
                          </small>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {schedule.status === RaffleDefaultDrawScheduleStatusEnum.FAILED && (
              <div className="alert alert-danger mt-3 mb-0">
                {schedule.message || "Ocorreu um erro ao processar o sorteio."}
              </div>
            )}

            {schedule.status ===
              RaffleDefaultDrawScheduleStatusEnum.PROCESSING && (
              <div className="alert alert-info mt-3 mb-0">
                <FaClock className="me-2" />
                O sorteio ainda está em processamento.
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
